/** @file Creates an IK constraint on a 1- or 2-bone chain aiming at a target bone (§6.5 checked). */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import type { Doc, Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { defineCreate } from '../_shared/defineCreate';
import { assertIkChain } from './assertIkChain';
import { editWithoutIkCycle } from './editWithoutIkCycle';
import { resolveChain } from './resolveChain';
import { warnIkChain } from './warnIkChain';

const ref = z.string().min(1);

const create = defineCreate({
  domain: 'ik',
  kind: 'ik',
  description:
    'Create an IK constraint: bones [parent] or [parent, child] (direct child, inherit normal), a target bone outside the chain; mix 1, softness 0, bendPositive true, compress/stretch/uniform false and order last unless given.',
  schema: z
    .object({
      id: z.string().exactOptional(),
      name: z.string().min(1),
      bones: z.union([z.tuple([ref]), z.tuple([ref, ref])]),
      target: ref,
      order: z.number().int().nonnegative().exactOptional(),
      mix: prim.unit.default(1),
      softness: prim.finite.nonnegative().default(0),
      bendPositive: z.boolean().default(true),
      compress: z.boolean().default(false),
      stretch: z.boolean().default(false),
      uniform: z.boolean().default(false),
    })
    .strict(),
  build(input, ctx) {
    const { mix, softness, bendPositive, compress, stretch, uniform } = input;
    const bones = resolveChain(ctx, input.bones);
    return { type: 'ik', name: input.name, order: input.order ?? 0, bones, target: ctx.resolve('bone', input.target), mix, softness, bendPositive, compress, stretch, uniform };
  },
});

/** One past the highest order of the other constraints, so a new constraint runs last. */
function nextOrder(doc: Doc, self: Id): number {
  return Object.entries(doc.constraints).reduce((max, [id, c]) => (id === self ? max : Math.max(max, c.order + 1)), 0);
}

/**
 * ik.create: chain rules first (IK_INVALID at the offending field), then the factory insert and order (CYCLE at
 * `target` when the new constraint and another one read each other's bones, §6.6), then the §6.5 INFOs.
 */
export const command = defineCommand({
  ...create,
  apply(draft, input, ctx) {
    const bones = resolveChain(ctx, input.bones);
    assertIkChain(draft, { ik: input.name, bones, target: ctx.resolve('bone', input.target), at: '' });
    editWithoutIkCycle(draft, 'target', () => {
      create.apply(draft, input, ctx);
      const id = ctx.created[input.name] ?? '';
      const ik = draft.constraints[id];
      if (ik && input.order === undefined) ik.order = nextOrder(draft, id);
    });
    warnIkChain(draft, ctx, { bones, stretch: input.stretch, at: '' });
  },
});
